import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useBadgeDefinitions } from '@/hooks/useBadgeDefinitions';
import { useIssuedBadgeAwards } from '@/hooks/useBadgeAwards';
import { LoginArea } from '@/components/auth/LoginArea';
import { CreateBadgeForm } from '@/components/badges/CreateBadgeForm';
import { AwardBadgeForm } from '@/components/badges/AwardBadgeForm';
import { AcceptBadges } from '@/components/badges/AcceptBadges';
import { BadgeCard } from '@/components/badges/BadgeCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldPlus, Award, Trophy, Layers, ExternalLink } from 'lucide-react';

export default function Index() {
  useSeoMeta({
    title: 'Badge Forge — NIP-58 Nostr Badges Manager',
    description:
      'Create, award, and accept NIP-58 Nostr badges. Manage your badge definitions and distribute them to any Nostr identity.',
  });

  const { user, metadata } = useCurrentUser();
  const { data: badges = [], isLoading: loadingBadges } = useBadgeDefinitions(user?.pubkey);
  const { data: awards = [] } = useIssuedBadgeAwards(user?.pubkey);

  const [awardTab, setAwardTab] = useState<'my-badges' | 'create' | 'award' | 'accept'>('my-badges');
  const [preselectedD, setPreselectedD] = useState<string | undefined>(undefined);

  const handleAwardBadge = (d: string) => {
    setPreselectedD(d);
    setAwardTab('award');
  };

  // Group awards by badge
  const awardsByBadge = badges.reduce<Record<string, typeof awards>>((acc, badge) => {
    const badgeRef = `30009:${user?.pubkey}:${badge.d}`;
    acc[badge.d] = awards.filter((a) => a.badgeRef === badgeRef);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-none tracking-tight">Badge Forge</h1>
              <p className="text-xs text-muted-foreground leading-none mt-0.5">NIP-58 Badges Manager</p>
            </div>
          </div>
          <LoginArea className="max-w-52" />
        </div>
      </header>

      <main className="flex-1 container mx-auto py-8 px-4 sm:px-6">
        {!user ? (
          /* Hero / not logged in */
          <div className="max-w-xl mx-auto mt-12 text-center space-y-6">
            <div className="mx-auto h-20 w-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Trophy className="h-10 w-10 text-primary" />
            </div>
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Create &amp; Award Nostr Badges</h2>
              <p className="mt-2 text-muted-foreground text-lg">
                Badge Forge lets you define, issue, and accept{' '}
                <a
                  href="https://github.com/nostr-protocol/nips/blob/master/58.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-4 hover:text-foreground"
                >
                  NIP-58
                </a>{' '}
                badges on Nostr — recognition tokens you can send to any Nostr identity.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
              {[
                {
                  icon: <ShieldPlus className="h-5 w-5 text-primary" />,
                  title: 'Define badges',
                  desc: 'Create badge definitions with a name, image, and description.',
                },
                {
                  icon: <Award className="h-5 w-5 text-primary" />,
                  title: 'Award them',
                  desc: 'Send badges to one or many npubs with a single event.',
                },
                {
                  icon: <Trophy className="h-5 w-5 text-primary" />,
                  title: 'Accept & display',
                  desc: 'Recipients choose which badges to show on their profile.',
                },
              ].map((f) => (
                <Card key={f.title} className="border-border/40 bg-card/50">
                  <CardContent className="p-4 space-y-1.5">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      {f.icon}
                    </div>
                    <p className="font-semibold text-sm">{f.title}</p>
                    <p className="text-xs text-muted-foreground">{f.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <LoginArea className="max-w-60" />
            </div>
          </div>
        ) : (
          /* Logged-in UI */
          <div className="max-w-3xl mx-auto space-y-6">
            {/* User greeting */}
            <div className="flex items-center gap-3">
              {metadata?.picture && (
                <img
                  src={metadata.picture}
                  alt={metadata.name ?? 'Avatar'}
                  className="h-10 w-10 rounded-full object-cover border border-border/40"
                />
              )}
              <div>
                <p className="font-semibold leading-none">
                  {metadata?.name ?? metadata?.display_name ?? 'Anon'}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {badges.length} badge{badges.length !== 1 ? 's' : ''} defined ·{' '}
                  {awards.length} award event{awards.length !== 1 ? 's' : ''} issued
                </p>
              </div>
            </div>

            <Tabs value={awardTab} onValueChange={(v) => setAwardTab(v as typeof awardTab)}>
              <TabsList className="grid grid-cols-4 h-10">
                <TabsTrigger value="my-badges" className="text-xs sm:text-sm">
                  <Layers className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />
                  My Badges
                </TabsTrigger>
                <TabsTrigger value="create" className="text-xs sm:text-sm">
                  <ShieldPlus className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />
                  Create
                </TabsTrigger>
                <TabsTrigger value="award" className="text-xs sm:text-sm">
                  <Award className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />
                  Award
                </TabsTrigger>
                <TabsTrigger value="accept" className="text-xs sm:text-sm">
                  <Trophy className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />
                  Received
                </TabsTrigger>
              </TabsList>

              {/* My Badges tab */}
              <TabsContent value="my-badges" className="mt-4 space-y-3">
                {loadingBadges ? (
                  [...Array(3)].map((_, i) => (
                    <Card key={i} className="border-border/40">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Skeleton className="h-16 w-16 rounded-xl" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-36" />
                            <Skeleton className="h-3 w-20" />
                            <Skeleton className="h-3 w-56" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : badges.length === 0 ? (
                  <Card className="border-dashed border-border/60">
                    <CardContent className="py-12 text-center">
                      <Trophy className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                      <p className="font-medium">No badges yet</p>
                      <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
                        Create your first badge definition using the Create tab.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  badges.map((badge) => (
                    <BadgeCard
                      key={badge.d}
                      badge={badge}
                      awards={awardsByBadge[badge.d] ?? []}
                      onAward={handleAwardBadge}
                    />
                  ))
                )}
              </TabsContent>

              {/* Create tab */}
              <TabsContent value="create" className="mt-4">
                <CreateBadgeForm onSuccess={() => setAwardTab('my-badges')} />
              </TabsContent>

              {/* Award tab */}
              <TabsContent value="award" className="mt-4">
                <AwardBadgeForm
                  key={preselectedD}
                  preselectedBadgeD={preselectedD}
                  onSuccess={() => {
                    setPreselectedD(undefined);
                    setAwardTab('my-badges');
                  }}
                />
              </TabsContent>

              {/* Received/Accept tab */}
              <TabsContent value="accept" className="mt-4">
                <AcceptBadges />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-4 mt-8">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Shakespeare
            </a>
          </span>
          <a
            href="https://github.com/nostr-protocol/nips/blob/master/58.md"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            NIP-58 Specification
          </a>
        </div>
      </footer>
    </div>
  );
}
